import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { logActivity } from "@/lib/logActivity";
import JobModal, { type Job } from "@/pages/admin/jobs/components/JobModal";
import StatusBadge from "@/components/base/StatusBadge";
import Swal from "sweetalert2";
import type { Client, ClientJob } from "../types";

interface Props {
  client: Client;
  tenantId: string;
}

const DEFAULT_STEPS = [
  { step_key: "contract_deposit", title: "Contract / Deposit", sort_order: 0 },
  { step_key: "shoot_scheduled",  title: "Shoot Scheduled",    sort_order: 1 },
  { step_key: "shoot_completed",  title: "Shoot Completed",    sort_order: 2 },
  { step_key: "editing",          title: "Editing",            sort_order: 3 },
  { step_key: "client_selection", title: "Client Selection",   sort_order: 4 },
  { step_key: "final_delivery",   title: "Final Delivery",     sort_order: 5 },
  { step_key: "completed",        title: "Completed",          sort_order: 6 },
];

export default function JobsTab({ client, tenantId }: Props) {
  const navigate = useNavigate();
  const [jobs, setJobs]           = useState<ClientJob[]>([]);
  const [loading, setLoading]     = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [contracts, setContracts] = useState<{ id: string; title: string; contract_number: string; total_amount: number; balance_amount: number; status: string }[]>([]);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    const [jobsRes, contractsRes] = await Promise.all([
      supabase
        .from("jobs")
        .select("id, title, job_type, status, shoot_date, location, created_at")
        .eq("tenant_id", tenantId)
        .eq("client_id", client.id)
        .order("created_at", { ascending: false }),
      supabase
        .from("contracts")
        .select("id, title, contract_number, total_amount, balance_amount, status")
        .eq("tenant_id", tenantId)
        .eq("client_id", client.id),
    ]);
    setJobs((jobsRes.data as ClientJob[]) ?? []);
    setContracts((contractsRes.data as typeof contracts) ?? []);
    setLoading(false);
  }, [client.id, tenantId]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const handleSave = async (data: Job) => {
    const { data: created, error } = await supabase
      .from("jobs")
      .insert({
        tenant_id: tenantId,
        client_id: client.id,
        contract_id: data.contract_id || null,
        title: data.title,
        job_type: data.job_type,
        status: data.status || "scheduled",
        shoot_date: data.shoot_date || null,
        location: data.location || null,
        notes: data.notes || null,
      })
      .select("id, title, job_type, status, shoot_date, location, created_at")
      .maybeSingle();

    if (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#fff", color: "#0f172a" });
      return;
    }
    if (created) {
      const steps = DEFAULT_STEPS.map((s) => ({ ...s, tenant_id: tenantId, job_id: (created as ClientJob).id, status: "pending" }));
      await supabase.from("job_steps").insert(steps);
      setJobs((prev) => [created as ClientJob, ...prev]);
      logActivity({ tenantId, action: "created", entity: "job", entityId: (created as ClientJob).id, entityName: data.title });
    }
    setModalOpen(false);
    Swal.fire({ title: "Job created!", icon: "success", timer: 1500, showConfirmButton: false, background: "#fff", color: "#0f172a" });
  };

  const fmtDate = (d: string | null) => {
    if (!d) return null;
    return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const statusColors: Record<string, string> = {
    scheduled:       "bg-sky-400/10 text-sky-600 border-sky-400/20",
    in_progress:     "bg-amber-400/10 text-amber-600 border-amber-400/20",
    editing:         "bg-violet-400/10 text-violet-600 border-violet-400/20",
    waiting_client:  "bg-orange-400/10 text-orange-600 border-orange-400/20",
    delivered:       "bg-emerald-400/10 text-emerald-600 border-emerald-400/20",
    completed:       "bg-emerald-400/10 text-emerald-700 border-emerald-400/20",
    cancelled:       "bg-slate-200 text-slate-500 border-slate-300",
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <p className="text-slate-400 text-sm">{jobs.length} job{jobs.length !== 1 ? "s" : ""}</p>
        <button
          onClick={() => setModalOpen(true)}
          className="flex items-center gap-1.5 px-3 py-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          New Job
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-8 justify-center">
          <div className="w-4 h-4 border-2 border-slate-200 border-t-slate-400 rounded-full animate-spin"></div>
          Loading jobs…
        </div>
      ) : jobs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
          <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-[#C8A96E]/10">
            <i className="ri-camera-3-line text-[#9a7840] text-xl"></i>
          </div>
          <p className="text-slate-700 font-medium text-sm">No jobs yet</p>
          <p className="text-slate-400 text-xs">Create the first shoot for {client.full_name}</p>
          <button
            onClick={() => setModalOpen(true)}
            className="flex items-center gap-1.5 px-4 py-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Create Job
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {jobs.map((job) => (
            <div key={job.id} className="bg-white rounded-xl border border-slate-200 hover:border-slate-300 transition-all p-4 flex items-center gap-4">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#C8A96E]/10 shrink-0">
                <i className="ri-camera-3-line text-[#9a7840] text-base"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-slate-800 font-medium text-sm mb-0.5 truncate">{job.title}</p>
                <div className="flex items-center gap-3 text-xs text-slate-400 flex-wrap">
                  <span>{job.job_type}</span>
                  {job.shoot_date && <span><i className="ri-calendar-line mr-1"></i>{fmtDate(job.shoot_date)}</span>}
                  {job.location && <span><i className="ri-map-pin-line mr-1"></i>{job.location}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${statusColors[job.status] ?? "bg-slate-100 text-slate-500 border-slate-200"}`}>
                  {job.status.replace(/_/g, " ")}
                </span>
                <button
                  onClick={() => navigate(`/admin/jobs/${job.id}`)}
                  className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#9a7840] hover:bg-[#C8A96E]/10 rounded-lg transition-all cursor-pointer"
                  title="Open job"
                >
                  <i className="ri-arrow-right-line text-sm"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <JobModal
          job={null}
          isNew
          clients={[{ id: client.id, full_name: client.full_name }]}
          contracts={contracts}
          onClose={() => setModalOpen(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}